import { useCallback, useContext, useMemo } from "react";
import { isString } from "lodash";
import { UserContext } from "../amplify/authenticator-provider";
import { isAllLocationSelected } from "../library/helpers";

// `location.settings` comes back either as an object or as a JSON string
// depending on the query — normalise before reading keys off it.
const parseLocationSettings = settings => {
  if (!settings) return {};
  if (!isString(settings)) return settings;
  try {
    return JSON.parse(settings);
  } catch (error) {
    return {};
  }
};

const useLocationHelpers = () => {
  const { user, isUserIsBeingSet } = useContext(UserContext);

  const currentLocationId = sessionStorage.getItem("currentLocationId");
  const isAllLocation = isAllLocationSelected();
  const isUserLoaded = !isUserIsBeingSet;

  const currentLocation = useMemo(() => {
    if (!user?.locations?.length || !currentLocationId) return null;

    if (isAllLocation) return null;

    return (
      user.locations.find(loc => String(loc.location?.id) === String(currentLocationId))
        ?.location || null
    );
  }, [user?.locations, currentLocationId, isAllLocation]);

  // The user's home/primary location — `user.location` (singular) is the
  // BE-resolved "primary" concept; look it up against `user.locations` to
  // get the fuller location payload (timezone, etc). Falls back to the
  // first entry in `user.locations` if that lookup ever misses.
  const primaryLocation = useMemo(() => {
    if (!user?.locations?.length) return null;
    const byId = user?.location?.id
      ? user.locations.find(loc => String(loc.location?.id) === String(user.location.id))?.location
      : null;
    return byId || user.locations[0]?.location || null;
  }, [user?.locations, user?.location]);

  // The locations this user actually has access to, as antd Select options.
  // `user.locations` is the auth user's access list — distinct from
  // GET_LOCATIONS, which returns every location in the company.
  const userLocationOptions = useMemo(
    () =>
      (user?.locations || [])
        .map(entry => entry?.location)
        .filter(location => location?.id)
        .map(location => ({
          value: String(location.id),
          label: location.title || location.name || `Location ${location.id}`,
        })),
    [user?.locations]
  );

  // Timezone for whatever's currently active: the explicitly-selected
  // single location, falling back to the user's primary location when
  // "All locations" is selected (or no location is resolved yet).
  const currentLocationTimezone = currentLocation?.timezone || primaryLocation?.timezone;

  // Look up any location the user has access to, by id.
  const getLocationById = useCallback(
    id =>
      (id && user?.locations?.find(loc => String(loc.location?.id) === String(id))?.location) ||
      null,
    [user?.locations]
  );

  // Parsed settings of the location that applies right now: the selected single
  // location, or the user's PRIMARY location under "All locations" (where there
  // is no single selected location). `null` until it resolves — callers that
  // WRITE state off a setting should wait for that rather than read a
  // not-yet-loaded location as "unset".
  const locationSettings = useMemo(() => {
    const location = currentLocation || (isAllLocation ? primaryLocation : null);
    return location ? parseLocationSettings(location.settings) : null;
  }, [currentLocation, isAllLocation, primaryLocation]);

  return {
    currentLocation,
    currentLocationId,
    currentLocationTimezone,
    primaryLocation,
    locationSettings,
    getLocationById,
    isAllLocation,
    isUserLoaded,
    userLocationOptions,
  };
};

export default useLocationHelpers;
